import { useState } from 'react';
import { X } from 'lucide-react';
import type { Agent, DayOfWeek, Studio } from '@/domain/types';

const DAYS: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL: Record<DayOfWeek, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

const inputCls =
  'mt-1 block w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream';
const labelCls = 'block text-[10px] font-inter uppercase tracking-widest text-cream-soft';

interface Props {
  studio: Studio;
  agents: Agent[];
  onCancel: () => void;
  onSave: (studio: Studio) => void;
}

export default function EditStudioModal({ studio, agents, onCancel, onSave }: Props) {
  // draft carries every Studio field; id/lat/lon/artistCount/assignedAgentIds/
  // isBlackBook have no UI and pass through unchanged on save. The optional
  // gettingHere/prepNotes sub-objects are edited as flat atoms below and only
  // reassembled in handleSave (so blank inputs can collapse back to undefined).
  const [draft, setDraft] = useState<Studio>(studio);
  const [parking, setParking] = useState(studio.gettingHere?.parking ?? '');
  const [transport, setTransport] = useState(studio.gettingHere?.transport ?? '');
  const [directions, setDirections] = useState(studio.gettingHere?.directions ?? '');
  const [eatBeforehand, setEatBeforehand] = useState(studio.prepNotes?.eatBeforehand ?? false);
  const [hydrate, setHydrate] = useState(studio.prepNotes?.hydrate ?? false);
  const [customPrep, setCustomPrep] = useState(studio.prepNotes?.custom ?? '');

  const set = <K extends keyof Studio>(key: K, value: Studio[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const setDay = (day: DayOfWeek, slot: { open: string; close: string } | null) =>
    setDraft((d) => ({ ...d, operatingHours: { ...d.operatingHours, [day]: slot } }));

  const handleSave = () => {
    const gettingHere =
      parking.trim() || transport.trim() || directions.trim()
        ? {
            parking: parking.trim() || undefined,
            transport: transport.trim() || undefined,
            directions: directions.trim() || undefined,
          }
        : undefined;
    const prepNotes =
      eatBeforehand || hydrate || customPrep.trim()
        ? {
            eatBeforehand: eatBeforehand || undefined,
            hydrate: hydrate || undefined,
            custom: customPrep.trim() || undefined,
          }
        : undefined;
    onSave({ ...draft, gettingHere, prepNotes });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-black/70 p-4 overflow-y-auto"
      data-testid="studio-edit-modal"
      onClick={onCancel}
    >
      <div
        className="panel p-6 w-full max-w-2xl space-y-5 my-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between">
          <div>
            <p className="label-small-caps text-gold-bright">Edit studio</p>
            <h2 className="heading-display text-2xl text-cream mt-1">{studio.name}</h2>
          </div>
          <button type="button" onClick={onCancel} className="btn-quiet" aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <div className="grid grid-cols-3 gap-3">
          <label className={labelCls}>
            Name
            <input
              className={inputCls}
              value={draft.name}
              onChange={(e) => set('name', e.target.value)}
              data-testid="studio-edit-name"
              autoFocus
            />
          </label>
          <label className={labelCls}>
            Brand
            <input className={inputCls} value={draft.brand} onChange={(e) => set('brand', e.target.value)} />
          </label>
          <label className={labelCls}>
            Branded number
            <input
              className={inputCls}
              value={draft.brandedNumber}
              onChange={(e) => set('brandedNumber', e.target.value)}
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className={labelCls}>
            Address
            <input className={inputCls} value={draft.address} onChange={(e) => set('address', e.target.value)} />
          </label>
          <label className={labelCls}>
            Postcode
            <input className={inputCls} value={draft.postcode} onChange={(e) => set('postcode', e.target.value)} />
          </label>
          <label className={labelCls}>
            State
            <input className={inputCls} value={draft.state} onChange={(e) => set('state', e.target.value)} />
          </label>
        </div>

        <div>
          <p className="label-small-caps text-gold-bright mb-2">Operating hours</p>
          <div className="space-y-2">
            {DAYS.map((day) => {
              const slot = draft.operatingHours[day];
              return (
                <div key={day} className="flex items-center gap-3">
                  <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft w-10">
                    {DAY_LABEL[day]}
                  </span>
                  <label className="flex items-center gap-1.5 text-[10px] font-inter uppercase tracking-widest text-cream-faint">
                    <input
                      type="checkbox"
                      checked={slot === null}
                      onChange={(e) =>
                        setDay(day, e.target.checked ? null : { open: '10:00', close: '18:00' })
                      }
                    />
                    Closed
                  </label>
                  {slot ? (
                    <>
                      <input
                        type="time"
                        value={slot.open}
                        onChange={(e) => setDay(day, { ...slot, open: e.target.value })}
                        className="bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1 text-sm text-cream"
                      />
                      <span className="text-cream-faint">–</span>
                      <input
                        type="time"
                        value={slot.close}
                        onChange={(e) => setDay(day, { ...slot, close: e.target.value })}
                        className="bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1 text-sm text-cream"
                      />
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className={labelCls}>
            Parking
            <input className={inputCls} value={parking} onChange={(e) => setParking(e.target.value)} />
          </label>
          <label className={labelCls}>
            Transport
            <input className={inputCls} value={transport} onChange={(e) => setTransport(e.target.value)} />
          </label>
          <label className={labelCls}>
            Wayfinding
            <input className={inputCls} value={directions} onChange={(e) => setDirections(e.target.value)} />
          </label>
        </div>

        <div>
          <p className="label-small-caps text-gold-bright mb-2">Prep reminders</p>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-[10px] font-inter uppercase tracking-widest text-cream-soft">
              <input
                type="checkbox"
                checked={eatBeforehand}
                onChange={(e) => setEatBeforehand(e.target.checked)}
              />
              Eat beforehand
            </label>
            <label className="flex items-center gap-1.5 text-[10px] font-inter uppercase tracking-widest text-cream-soft">
              <input type="checkbox" checked={hydrate} onChange={(e) => setHydrate(e.target.checked)} />
              Hydrate
            </label>
          </div>
          <label className={`${labelCls} mt-2`}>
            Custom note
            <input className={inputCls} value={customPrep} onChange={(e) => setCustomPrep(e.target.value)} />
          </label>
        </div>

        <label className={labelCls}>
          Manager
          <select
            className={inputCls}
            value={draft.managerId}
            onChange={(e) => set('managerId', e.target.value)}
            data-testid="studio-edit-manager"
          >
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.firstName} {a.lastName}
              </option>
            ))}
          </select>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="btn-quiet text-xs">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary text-xs"
            data-testid="studio-edit-save"
          >
            Save studio
          </button>
        </div>
      </div>
    </div>
  );
}
